import { Icon, List, Popup, Tab, TabList } from '@getsynapse/design-system';
import { TabPanel, TabPanels, Tabs } from '@reach/tabs';
import { useEffect, useState } from 'react';
import intl from 'react-intl-universal';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { BUDGET_TABS, PATHS, SLICE_STATUS } from 'utils/constants';
import {
  fetchBudgetAmountsByBudgetId,
  fetchBudgets,
  fetchBudgetSettings,
  fetchCostOfLaborActuals,
  fetchLineItemsAmountsByBudgetId,
  getLineItemStatus,
  getSelectedBudget,
  getShowBrokenModal,
  selectListBudget,
  setSelectedBudget,
  setShowBrokenModal,
} from '../../state/BudgetList/budgetListSlice';
import { fetchPrograms } from '../../state/Programs/programsSlice';
import { fetchProjects } from '../../state/Projects/projectsSlice';
import { getLDTeams } from '../../state/Teams/teamsSlice';
import { getVendors } from '../../state/Vendors/vendorsSlice';
import { BrokenRelationshipModal } from './components/BrokenRelationshipModal';
import useBrokenBudgetOptions from './hooks/useBrokenBudgetOptions';
import BudgetPlan from './tabs/Plan/BudgetPlan';
import BudgetReport from './tabs/Report/BudgetReport';

const BudgetDetailsPage = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();

  const { budgetId } = useParams<{ budgetId: string }>();

  const { brokenLineItems } = useBrokenBudgetOptions();
  const showBrokenModal = useSelector(getShowBrokenModal);

  const budgetList = useSelector(selectListBudget);
  const selectedBudget = useSelector(getSelectedBudget);
  const lineItemStatus = useSelector(getLineItemStatus);

  const queryParams = new URLSearchParams(window.location.search);
  const activeTab = queryParams.get('tab');

  const tabToString: any = [BUDGET_TABS.REPORT, BUDGET_TABS.PLAN];
  const [activeIndex, setActiveIndex] = useState(
    activeTab ? tabToString.indexOf(activeTab) : 1
  );
  const [displayBrokenRelationshipModal, setDisplayBrokenRelationshipModal] =
    useState(false);

  useEffect(() => {
    dispatch(fetchBudgets());
    dispatch(fetchProjects());
    dispatch(fetchPrograms());
    dispatch(getLDTeams());
    dispatch(getVendors());
    dispatch(fetchBudgetSettings());
    dispatch(setShowBrokenModal(true));
  }, [dispatch, budgetId]);

  useEffect(() => {
    if (budgetList && budgetList.length) {
      dispatch(setSelectedBudget(budgetId));
    }
  }, [dispatch, budgetList, budgetId]);

  useEffect(() => {
    setDisplayBrokenRelationshipModal(
      brokenLineItems.length > 0 && showBrokenModal
    );
  }, [brokenLineItems, showBrokenModal]);

  useEffect(() => {
    if (selectedBudget) {
      dispatch(fetchBudgetAmountsByBudgetId(selectedBudget.id));
      dispatch(fetchLineItemsAmountsByBudgetId(selectedBudget.id));
    }
  }, [dispatch, selectedBudget]);

  useEffect(() => {
    if (selectedBudget && lineItemStatus === SLICE_STATUS.SUCCESS) {
      dispatch(fetchCostOfLaborActuals(selectedBudget.id));
    }
  }, [lineItemStatus, selectedBudget, dispatch]);

  const onSelectedBudgetChange = (option: { label: string; value: string }) => {
    history.replace(`${PATHS.BUDGETS}/${option.value}`);
    dispatch(setSelectedBudget(option.value));
  };

  const budgetOptions = budgetList.map((budget) => ({
    label: budget.title,
    value: budget.id,
  }));

  const handleTabChange = (index: number) => {
    setActiveIndex(index);

    const tabKey = tabToString[index];
    history.push(`${location.pathname}?tab=${tabKey}`);
  };

  return (
    <div className='flex flex-col h-full'>
      <Tabs
        index={activeIndex}
        className='flex flex-col h-full'
        onChange={handleTabChange}
      >
        {budgetOptions.length !== 0 && (
          <div className='flex flex-row justify-between px-6 items-center mb-2'>
            <div className=''>
              {budgetOptions.length > 1 ? (
                <Popup
                  position='bottom-start'
                  popperProps={{ className: 'z-10' }}
                  trigger={({ isOpen }) => (
                    <div className='cursor-pointer flex flex-row items-center gap-x-2 text-xl font-bold'>
                      <div>{selectedBudget?.title}</div>
                      {isOpen ? (
                        <Icon name='caret-up-outline' />
                      ) : (
                        <Icon name='caret-down-outline' />
                      )}
                    </div>
                  )}
                >
                  {({ close }) => (
                    <List
                      options={budgetOptions ?? []}
                      className='text-xl'
                      data-cy='more-actions-list'
                      onSelectOption={(option) => {
                        onSelectedBudgetChange(option);
                        close();
                      }}
                    />
                  )}
                </Popup>
              ) : (
                <div className='text-xl font-bold'>{selectedBudget?.title}</div>
              )}
            </div>
            {budgetList.length !== 0 && (
              <div className='flex'>
                <TabList type='subHeader'>
                  <Tab index={0} type='subHeader'>
                    {intl.get('BUDGET_PAGE.TABS.REPORT')}
                  </Tab>
                  <Tab index={1} type='subHeader'>
                    {intl.get('BUDGET_PAGE.TABS.PLAN')}
                  </Tab>
                </TabList>
              </div>
            )}
          </div>
        )}

        <TabPanels className='h-full px-6 relative'>
          <TabPanel key={0} className='h-full'>
            <BudgetReport />
          </TabPanel>
          <TabPanel key={1} className='h-full'>
            <BudgetPlan />
          </TabPanel>
        </TabPanels>
      </Tabs>

      <BrokenRelationshipModal
        isOpen={displayBrokenRelationshipModal}
        closeModal={() => {
          setDisplayBrokenRelationshipModal(false);
          dispatch(setShowBrokenModal(false));
        }}
        onCancel={() => setDisplayBrokenRelationshipModal(false)}
      />
    </div>
  );
};

export default BudgetDetailsPage;
